import { get, post, b64uToBytes } from '/api.js';

export const state = () => get('/api/auth/state');
export const signOut = () => post('/api/auth/logout');

export async function register({ name, code }) {
  const options = await post('/api/auth/register/options', { name, code });
  options.challenge = b64uToBytes(options.challenge);
  options.user.id = b64uToBytes(options.user.id);
  (options.excludeCredentials || []).forEach(
    (c) => { c.id = b64uToBytes(c.id); });
  const credential = await navigator.credentials.create({ publicKey: options });
  // toJSON() produces exactly the shape py_webauthn parses, so there is no
  // hand-rolled serialisation to drift from the spec.
  return post('/api/auth/register/verify',
    { name, code, credential: credential.toJSON() });
}

/**
 * Sign in with a passkey. `phone: true` is the QR flow — GitHub issue #50.
 *
 * THE QR ALREADY EXISTED; IT WAS JUST UNREACHABLE. `hybrid` is WebAuthn's own
 * cross-device transport — scan a code, approve on the phone, the PC is in —
 * and the server has offered it all along (see HINTS in auth.py). But it is
 * offered THIRD, behind this machine's own biometrics, so a desktop with
 * Windows Hello set up goes straight to Hello and the phone sits under
 * "Other options" where nobody finds it. Asking for `hybrid` alone is what
 * makes the browser draw the code immediately.
 *
 * OVERRIDING THE SERVER'S HINTS IS NOT A HOLE. Hints steer the browser's
 * chooser and nothing else: they are advisory UI, carried in the options the
 * client already holds, and the assertion is verified identically whichever
 * authenticator produced it. There is nothing here for a client to gain by
 * lying, which is why this is a client-side flag rather than a second
 * endpoint.
 *
 * WHERE IT DOES NOTHING. `hints` is Chrome/Edge 128+; Safari and Firefox
 * ignore the field, so the phone button behaves exactly like the ordinary one
 * there — the same dialog, with the phone still somewhere inside it. That is
 * a browser we cannot steer, not a failure to handle.
 */
export async function signIn({ phone = false } = {}) {
  const options = await post('/api/auth/login/options');
  options.challenge = b64uToBytes(options.challenge);
  if (phone) options.hints = ['hybrid'];
  // No allowCredentials is sent on purpose: the authenticator offers whatever
  // discoverable passkey it holds for this domain, so nobody types a username.
  (options.allowCredentials || []).forEach(
    (c) => { c.id = b64uToBytes(c.id); });
  const credential = await navigator.credentials.get({ publicKey: options });
  return post('/api/auth/login/verify', { credential: credential.toJSON() });
}
